import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfigService } from '../app-config/app-config.service';
import { DateFormatService } from '../date/date-format.service';
import {
  IStockPeriodDay,
  ITimeRange,
  CDaysWeekList,
  IWeeklyOperatingHours
} from '../product/models/product-stock-periods.model';
import { IProduct } from '../product/models/product.model';
import { ProductService } from '../product/product.service';
import { IInitialConfig } from './initial-config.model';
import { ICheckoutProductCategory } from './models/checkout-product-category.model';
import { IVenueConfig } from './models/venue-config.model';
import { VenueConfigHelperService } from './venue-config-helper.service';
import { CBlankCurrency, ICurrencySettings } from '@core/checkout/currency-settings.model';
import { IVenueSetting } from '@core/venue-config/models/venue-setting.model';
import { IVenueCostSetting } from '@core/venue-config/models/venue-cost-setting.model';

@Injectable({
  providedIn: 'root'
})
export class VenueConfigService {
  private venueConfig!: IVenueConfig;
  private currencySettings: ICurrencySettings = CBlankCurrency;

  constructor(
    private http: HttpClient,
    private productService: ProductService,
    private dateFormatService: DateFormatService,
    private venueConfigHelperService: VenueConfigHelperService,
    private appConfigService: AppConfigService
  ) {}

  getVenueConfig(): IVenueConfig {
    return this.venueConfig;
  }

  getCurrencySettings(): ICurrencySettings {
    return this.currencySettings;
  }

  getCostSettings(): IVenueCostSetting {
    return this.venueConfig.costSettings;
  }

  setVenueConfig(config: IVenueConfig): void {
    this.venueConfig = config;
  }

  isFoodAndBeverageCheckout(): boolean {
    return this.venueConfig.checkoutType === 'foodAndBeverageCheckout';
  }

  isHomePageEnabled(): boolean {
    return this.venueConfig.homePage.isEnabled && !!this.venueConfig.homePage.widgets?.length;
  }

  isGuestAccountEnabled(): boolean {
    return !!this.venueConfig.venueSettings.isGuestAccountsEnabled;
  }

  isGuestCountEnabled(): boolean {
    return !!this.venueConfig.venueSettings.useGuestCount;
  }

  loadVenueConfig(config: IInitialConfig, discountCode?: string) {
    return combineLatest([
      this.http.get<IVenueConfig>(
        `${config.baseApiUrl}/checkout/${config.checkoutSlug}${discountCode ? `?code=${discountCode}` : ''}`
      ),
      this.http.get<IProduct[]>(`${config.baseApiUrl}/checkout/${config.checkoutSlug}/products`)
    ]).pipe(
      map(([venueConfig, products]: [IVenueConfig, IProduct[]]) => this.completeVenueConfig(venueConfig, products))
    );
  }

  /** Takes the venue Config and products fully populating the venueConfig with the products. */
  completeVenueConfig(venueConfig: IVenueConfig, products: IProduct[]): IVenueConfig {
    venueConfig.productsPage.addOns = this.venueConfigHelperService.venueAddonsMapToString(
      venueConfig.productsPage.addOns
    );
    products = this.venueConfigHelperService.filterProductsWithNoTickets(products);
    products = this.venueConfigHelperService.filterUniqueAddons(products);
    products = this.venueConfigHelperService.removeBlankImagesFromProducts(
      venueConfig,
      products,
      this.appConfigService.getAppValue<string>('defaultImageUrl')
    );
    products = this.venueConfigHelperService.mapPackageProducts(products);
    products = this.venueConfigHelperService.addProductTypesToTickets(products);
    products = this.venueConfigHelperService.addProductsToPartyPackageItems(products);
    products = this.venueConfigHelperService.addProductsToProductOptionSets(products);
    products = this.venueConfigHelperService.mapWalletProducts(products);
    products = this.venueConfigHelperService.mapPricingTimeRuleToTickets(products, venueConfig.pricingRules);
    venueConfig.venueSettings.timeZoneOffsetMinutes = this.dateFormatService.calcTimeZoneOffsetMinutes(
      venueConfig.venueSettings.timeZoneOffset
    );

    this.productService.setProductList(products);

    let fullConfig: IVenueConfig = this.venueConfigHelperService.addProductsToProductPage(venueConfig, products);
    fullConfig = this.venueConfigHelperService.addProductsToWidget(fullConfig, products);
    fullConfig = this.checkOperatingMenuTime(fullConfig);
    this.setCurrencySettings(fullConfig.venueSettings);

    return fullConfig;
  }

  /** Determines if the menu is currently available based on assigned stock period or falls back to already calculated venue open */
  isMenuAvailable(category: ICheckoutProductCategory, venueTimeZoneOffsetMin: number, isVenueOpen: boolean): boolean {
    if (!category.stockPeriod) {
      return isVenueOpen;
    }

    const currentVenueTimeMs = this.dateFormatService.venueTimeMs(venueTimeZoneOffsetMin);
    const currentVenueTime = new Date(currentVenueTimeMs);
    const currentDateString = this.dateFormatService.dateToStringDateOnlyHyphen(currentVenueTime);
    const currentVenueDay = CDaysWeekList[currentVenueTime.getDay()] as keyof IWeeklyOperatingHours<IStockPeriodDay>;
    const dayRange = category.stockPeriod.days[currentVenueDay];
    if (dayRange.closed) {
      return false;
    }

    return dayRange.timeRanges.some((dayRange: ITimeRange) => {
      const startHour = this.dateFormatService.numberToHourString(dayRange.startTime);
      const endHour = this.dateFormatService.numberToHourString(dayRange.endTime);

      const venueStartDateTime = new Date(`${currentDateString}T${startHour}:00`).getTime();
      const venueEndDateTime = new Date(`${currentDateString}T${endHour}:00`).getTime();

      return !(currentVenueTimeMs < venueStartDateTime || currentVenueTimeMs > venueEndDateTime);
    });
  }

  /** Calculate and update the venue Config for the current open status of the venue and menus */
  checkOperatingMenuTime(config: IVenueConfig): IVenueConfig {
    if (config.checkoutType === 'foodAndBeverageCheckout') {
      config.productsPage.productCategories = config.productsPage.productCategories?.map(
        (productCategory: ICheckoutProductCategory) => {
          productCategory.isOpenNow = this.isMenuAvailable(
            productCategory,
            config.venueSettings.timeZoneOffsetMinutes || 0,
            config.venueSettings.isVenueOpen
          );
          return productCategory;
        }
      );
    }

    return config;
  }

  private setCurrencySettings(venueSettings: IVenueSetting): void {
    this.currencySettings = {
      currencyFractionSize: `1.${venueSettings.currencyFractionSize}-${venueSettings.currencyFractionSize}`,
      currencyCode: venueSettings.currencyCode,
      currencySymbol: venueSettings.currencySymbol
    };
  }
}
